import { useMemo } from 'react';

interface Props {
  query: string;
  visible: boolean;
  onSelect: (suggestion: string) => void;
}

/* Seeded suggestion bank — maps keywords to related searches */
const SUGGESTION_MAP: Array<{ keywords: string[]; suggestions: string[] }> = [
  {
    keywords: ['quantum', 'qubit', 'superposition'],
    suggestions: [
      'quantum computing applications in cryptography',
      'quantum supremacy explained',
      'quantum vs classical computing comparison',
      'quantum error correction methods',
      'IBM quantum processor latest developments',
    ],
  },
  {
    keywords: ['machine learning', 'ml', 'neural', 'deep learning', 'ai model'],
    suggestions: [
      'machine learning algorithms overview',
      'deep learning vs machine learning differences',
      'neural network architecture types',
      'AI model training techniques 2024',
      'machine learning in healthcare applications',
    ],
  },
  {
    keywords: ['climate', 'carbon', 'greenhouse', 'global warming', 'environment'],
    suggestions: [
      'climate change mitigation strategies 2024',
      'carbon capture technology advancements',
      'renewable energy vs fossil fuels statistics',
      'global warming effects on biodiversity',
      'net zero emissions roadmap countries',
    ],
  },
  {
    keywords: ['crispr', 'gene', 'dna', 'genomics', 'biotech'],
    suggestions: [
      'CRISPR gene editing latest breakthroughs',
      'genomics applications in medicine',
      'gene therapy clinical trials 2024',
      'DNA sequencing cost reduction history',
      'synthetic biology future applications',
    ],
  },
  {
    keywords: ['crypto', 'bitcoin', 'blockchain', 'ethereum', 'defi'],
    suggestions: [
      'Bitcoin price history and market cap',
      'blockchain technology use cases beyond crypto',
      'DeFi protocols explained simply',
      'Ethereum vs Bitcoin technical differences',
      'crypto regulation updates worldwide',
    ],
  },
  {
    keywords: ['space', 'nasa', 'mars', 'rocket', 'satellite', 'moon'],
    suggestions: [
      'SpaceX Starship latest launch updates',
      'Mars colonization timeline feasibility',
      'NASA Artemis moon mission progress',
      'James Webb telescope discoveries 2024',
      'commercial space travel companies compared',
    ],
  },
  {
    keywords: ['cancer', 'tumor', 'oncology', 'chemotherapy', 'immunotherapy'],
    suggestions: [
      'cancer immunotherapy breakthroughs 2024',
      'early cancer detection methods comparison',
      'CAR-T cell therapy clinical results',
      'cancer prevention diet and lifestyle factors',
      'precision oncology targeted therapy examples',
    ],
  },
  {
    keywords: ['mental health', 'depression', 'anxiety', 'therapy', 'psychology'],
    suggestions: [
      'cognitive behavioral therapy effectiveness research',
      'mental health apps clinical evidence review',
      'depression treatment options compared',
      'anxiety disorder prevalence statistics',
      'mindfulness meditation neuroscience research',
    ],
  },
  {
    keywords: ['gpt', 'llm', 'chatgpt', 'large language', 'generative'],
    suggestions: [
      'GPT-4 vs GPT-3 capabilities comparison',
      'large language model training data overview',
      'generative AI content moderation challenges',
      'LLM hallucination causes and solutions',
      'open source LLMs performance benchmarks',
    ],
  },
  {
    keywords: ['electric', 'ev', 'tesla', 'battery', 'charging'],
    suggestions: [
      'electric vehicle range comparison 2024',
      'EV battery technology solid state progress',
      'Tesla autopilot safety statistics',
      'electric vehicle charging infrastructure growth',
      'EV vs hybrid total cost of ownership',
    ],
  },
];

/* Fallback generic suggestions built from the raw query */
function buildFallbackSuggestions(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return [
    `${q} latest research 2024`,
    `${q} overview and applications`,
    `${q} best practices guide`,
    `${q} recent developments`,
    `how does ${q} work`,
  ];
}

export default function RelatedSearchSuggestions({ query, visible, onSelect }: Props) {
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];

    for (const entry of SUGGESTION_MAP) {
      if (entry.keywords.some((kw) => q.includes(kw))) {
        /* return top 5, filtering any that start with the exact query */
        return entry.suggestions.filter((s) => !s.toLowerCase().startsWith(q)).slice(0, 5);
      }
    }
    return buildFallbackSuggestions(q).slice(0, 4);
  }, [query]);

  if (!visible || suggestions.length === 0) return null;

  return (
    <div
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 z-30 overflow-hidden"
      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-fuchsia-50">
        <i className="ri-sparkling-line text-violet-500 text-sm"></i>
        <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Related Searches</span>
      </div>

      {suggestions.map((s, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(s)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-50 transition-colors group border-b border-slate-50 last:border-0 cursor-pointer text-left"
        >
          <div className="w-6 h-6 flex items-center justify-center shrink-0">
            <i className="ri-search-line text-slate-400 group-hover:text-violet-600 text-sm transition-colors"></i>
          </div>
          <span className="flex-1 text-sm text-slate-700 group-hover:text-violet-800 transition-colors truncate">
            {s}
          </span>
          <div className="w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <i className="ri-arrow-right-up-line text-violet-400 text-sm"></i>
          </div>
        </button>
      ))}
    </div>
  );
}
